<template>
  <div class="flex flex-col h-full min-h-0 text-xs">
    <LoadingIndicator v-if="loading" />

    <p v-else-if="error" class="p-4 text-red-800">{{ error }}</p>

    <template v-else>
      <div class="flex-none bg-white border-b border-gray-200 p-4">
        <div class="flex justify-between">
          <span class="text-gray-500">{{ metaLine }}</span>
          <button type="button" class="text-primary-700 hover:underline" @click="fetchPortfolio">
            Refresh
          </button>
        </div>

        <div class="flex gap-8 mt-3">
          <div v-for="stat in stats" :key="stat.label">
            <div class="font-semibold">{{ stat.value }}</div>
            <div class="text-gray-500">{{ stat.label }}</div>
          </div>
        </div>

        <div class="flex gap-4 mt-3">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            :class="tab === t.key ? 'font-medium' : 'text-gray-500'"
            @click="tab = t.key"
          >
            {{ t.label }} ({{ t.count }})
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-auto">
        <div class="p-4">
          <!-- Clicking a card filters the whole board to that customer. -->
          <div class="flex gap-2 mb-4">
            <button
              v-for="card in customers"
              :key="card.customer"
              type="button"
              class="border p-2 text-left"
              :class="customer === card.customer
                ? 'border-gray-900 bg-gray-100' : 'border-gray-200 hover:bg-gray-50'"
              @click="customer = customer === card.customer ? null : card.customer"
            >
              <div class="font-medium">{{ card.customer }}</div>
              <div class="text-gray-500">
                {{ card.total }} open · {{ card.unnoticed }} unnoticed
              </div>
            </button>
          </div>

          <Hades_Charts :flow="flow" :pull-requests="pullRequests" :customer="customer" />
        </div>

      <div class="flex min-h-0" style="height: 640px">
        <nav class="w-56 flex-none bg-white border-r border-gray-200 overflow-y-auto p-2">
          <div class="text-gray-500 p-1">{{ railTitle }}</div>
          <button
            v-for="item in rail"
            :key="item.value"
            type="button"
            class="w-full flex justify-between p-1"
            :class="item.active ? 'bg-gray-100' : 'hover:bg-gray-50'"
            @click="selectRail(item.value)"
          >
            <span>{{ item.label }}</span>
            <span class="text-gray-500">{{ item.count }}</span>
          </button>
        </nav>

        <main class="flex-1 min-w-0 overflow-auto p-4">
          <!-- The finding, stated rather than decorated. -->
          <p v-if="tab === 'engineering'" class="mb-3 text-gray-600">
            {{ unlinkedCount }} of {{ prTotal }} pull requests have no traceable
            customer request.
          </p>

          <Hades_TicketTable
            v-if="tab === 'requirements'"
            :tickets="visibleTickets"
            :selected-id="selectedTicketId"
            @select="selectedTicketId = $event"
          />
          <Hades_PRTable
            v-else
            :pull-requests="visiblePrs"
            :selected-id="selectedPrId"
            @select="selectedPrId = $event"
          />
        </main>

        <Hades_Detail
          :ticket="tab === 'requirements' ? selectedTicket : null"
          :pull-request="tab === 'engineering' ? selectedPr : null"
          @closed="fetchPortfolio"
        />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Hades_TicketTable from './Hades_TicketTable.vue';
import Hades_PRTable from './Hades_PRTable.vue';
import Hades_Detail from './Hades_Detail.vue';
import Hades_Charts from './Hades_Charts.vue';
import { CATEGORY_LABELS, QUEUE_LABELS, QUEUE_ORDER, label } from './hades_constants';

export default {
  name: 'Support_Hades',
  components: {
    LoadingIndicator, Hades_TicketTable, Hades_PRTable, Hades_Detail, Hades_Charts,
  },
  data() {
    return {
      loading: true,
      error: null,
      run: null,
      tickets: [],
      pullRequests: [],
      queueCounts: {},
      categoryCounts: {},
      unlinkedCount: 0,
      prTotal: 0,
      investigatedCount: 0,
      requirementCount: 0,
      flow: [],
      customers: [],
      customer: null,
      tab: 'requirements',
      queueFilter: 'all',
      categoryFilter: 'all',
      selectedTicketId: null,
      selectedPrId: null,
    };
  },
  computed: {
    metaLine() {
      if (!this.run) return 'No completed run yet — showing requirements only.';
      const when = this.run.completed_at
        ? new Date(this.run.completed_at).toLocaleString() : 'in progress';
      return `Run ${this.run.id} · ${this.run.model} · ${when}`;
    },
    stats() {
      const closeable = this.tickets.filter(
        (t) => (t.queues || []).includes('shipped_record_stale')).length;
      return [
        { label: 'Requirements', value: this.requirementCount },
        { label: 'Investigated', value: `${this.investigatedCount} / ${this.requirementCount}` },
        { label: 'Ready to close', value: closeable },
        { label: 'Unnoticed', value: this.queueCounts.unnoticed || 0 },
        { label: 'Unknown', value: this.queueCounts.needs_evidence || 0 },
        { label: 'PRs unlinked', value: `${this.unlinkedCount} / ${this.prTotal}` },
      ];
    },
    tabs() {
      return [
        { key: 'requirements', label: 'Requirements', count: this.tickets.length },
        { key: 'engineering', label: 'Engineering', count: this.pullRequests.length },
      ];
    },
    railTitle() {
      return this.tab === 'requirements' ? 'Queue' : 'Category';
    },
    rail() {
      const isReq = this.tab === 'requirements';
      const counts = isReq ? this.queueCounts : this.categoryCounts;
      const labels = isReq ? QUEUE_LABELS : CATEGORY_LABELS;
      const total = isReq ? this.tickets.length : this.pullRequests.length;
      const active = isReq ? this.queueFilter : this.categoryFilter;

      // Queues follow QUEUE_ORDER (by what demands attention). Categories have
      // no natural priority, so they sort by volume.
      const keys = isReq
        ? QUEUE_ORDER.filter((q) => counts[q])
        : Object.keys(counts).sort((a, b) => counts[b] - counts[a]);

      return [
        { value: 'all', label: 'All', count: total, active: active === 'all' },
        ...keys.map((key) => ({
          value: key,
          label: label(labels, key),
          count: counts[key],
          active: active === key,
        })),
      ];
    },
    visibleTickets() {
      let rows = this.tickets;
      if (this.customer) {
        rows = rows.filter((t) => (t.tenant_name || 'Platform') === this.customer);
      }
      if (this.queueFilter === 'all') return rows;
      return rows.filter((t) => (t.queues || []).includes(this.queueFilter));
    },
    visiblePrs() {
      if (this.categoryFilter === 'all') return this.pullRequests;
      return this.pullRequests.filter((p) => p.primary_category === this.categoryFilter);
    },
    selectedTicket() {
      return this.tickets.find((t) => t.id === this.selectedTicketId) || null;
    },
    selectedPr() {
      return this.pullRequests.find((p) => p.id === this.selectedPrId) || null;
    },
  },
  created() {
    this.fetchPortfolio();
  },
  methods: {
    async fetchPortfolio() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/hades/portfolio/');
        this.run = data.run;
        this.tickets = data.tickets || [];
        this.pullRequests = data.pull_requests || [];
        this.queueCounts = data.queue_counts || {};
        this.categoryCounts = data.category_counts || {};
        this.unlinkedCount = data.pull_requests_unlinked || 0;
        this.prTotal = data.pull_requests_total || 0;
        this.investigatedCount = data.investigated_count || 0;
        this.requirementCount = data.requirement_count || 0;
        this.flow = data.flow || [];
        this.customers = data.customers || [];
        // Keep the selection across a refresh where the row still exists —
        // closing a requirement refetches, and losing your place each time
        // makes working a queue painful.
        if (!this.selectedTicket) {
          this.selectedTicketId = this.visibleTickets[0]?.id ?? null;
        }
      } catch (e) {
        this.error = e.response?.data?.detail || 'Could not load the Hades portfolio.';
      } finally {
        this.loading = false;
      }
    },
    selectRail(value) {
      if (this.tab === 'requirements') {
        this.queueFilter = value;
        this.selectedTicketId = this.visibleTickets[0]?.id ?? null;
      } else {
        this.categoryFilter = value;
        this.selectedPrId = this.visiblePrs[0]?.id ?? null;
      }
    },
  },
};
</script>
